import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { GalleryGrid } from '../gallery/GalleryGrid';
import { useGalleryImages } from '../../hooks/useGalleryImages';
import { createLightSurvey, type LightSurveyDetail } from '../../services/lightSurveyService';
import './Lighting.css';

export interface NewSurveyFormProps {
  aquariumId: string;
  onCreated: (survey: LightSurveyDetail) => void;
  onCancel: () => void;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Optional number field: '' → null; otherwise the number, or undefined when it is not a number. */
function optionalNumber(value: string): number | null | undefined {
  if (value.trim() === '') return null;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/**
 * New PAR survey: a name, the photo the values go on (from the gallery — a
 * fixed-camera photo is best, the same view can be measured again later), and
 * how it was measured (sensor, lamp intensity, hours at full intensity).
 */
export function NewSurveyForm({ aquariumId, onCreated, onCancel }: NewSurveyFormProps) {
  const { t } = useTranslation();
  const gallery = useGalleryImages({ aquariumId, limit: 12 });
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(today());
  const [imageId, setImageId] = useState<string | null>(null);
  const [sensor, setSensor] = useState('');
  const [intensity, setIntensity] = useState('');
  const [hours, setHours] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () =>
      createLightSurvey(aquariumId, {
        title: title.trim(),
        measured_at: new Date(`${date}T12:00:00`).toISOString(),
        image_id: imageId,
        sensor: sensor.trim() || null,
        intensity_percent: optionalNumber(intensity) ?? null,
        photoperiod_hours: optionalNumber(hours) ?? null,
      }),
    onSuccess: onCreated,
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (title.trim() === '') next.title = String(t('lighting.new.titleRequired'));
    if (!date) next.date = String(t('lighting.new.dateRequired'));
    const i = optionalNumber(intensity);
    if (i === undefined || (i !== null && (!Number.isInteger(i) || i < 1 || i > 100))) next.intensity = String(t('lighting.new.intensityInvalid'));
    const h = optionalNumber(hours);
    if (h === undefined || (h !== null && (h <= 0 || h > 24))) next.hours = String(t('lighting.new.hoursInvalid'));
    setErrors(next);
    if (Object.keys(next).length === 0) create.mutate();
  };

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="4">
        <Input id="light-survey-title" label={t('lighting.new.title')} value={title} onChange={(e) => setTitle(e.target.value)} error={errors.title} maxLength={120} fullWidth placeholder={t('lighting.new.titlePlaceholder')} />
        <Input id="light-survey-date" type="date" label={t('lighting.new.date')} value={date} onChange={(e) => setDate(e.target.value)} error={errors.date} fullWidth />
        <Stack gap="2">
          <Text weight="medium">{t('lighting.new.photo')}</Text>
          <Text color="secondary" size="sm">{t('lighting.new.photoHint')}</Text>
          {gallery.error ? (
            <LoadErrorState title={t('lighting.new.galleryError')} onRetry={() => gallery.refetch?.()} retryLabel={t('lighting.retry')} titleLevel={3} />
          ) : (
            <div className="lighting__photo-picker">
              <GalleryGrid
                images={gallery.images}
                isLoading={gallery.isLoading || gallery.isFetchingNextPage}
                hasNextPage={gallery.hasNextPage}
                onLoadMore={gallery.fetchNextPage}
                onImageClick={() => undefined}
                selectionMode
                selectedIds={new Set(imageId ? [imageId] : [])}
                onToggleSelect={(id) => setImageId((current) => (current === id ? null : id))}
              />
            </div>
          )}
          {!gallery.isLoading && gallery.images.length === 0 && !gallery.error && <Text color="muted" size="sm">{t('lighting.new.noPhotos')}</Text>}
        </Stack>
        <Input id="light-survey-sensor" label={t('lighting.new.sensor')} value={sensor} onChange={(e) => setSensor(e.target.value)} maxLength={80} fullWidth placeholder={t('lighting.new.sensorPlaceholder')} />
        <Input
          id="light-survey-intensity"
          type="number"
          inputMode="numeric"
          min={1}
          max={100}
          label={t('lighting.new.intensity')}
          helperText={t('lighting.new.intensityHint')}
          value={intensity}
          onChange={(e) => setIntensity(e.target.value)}
          error={errors.intensity}
          fullWidth
        />
        <Input
          id="light-survey-hours"
          type="number"
          inputMode="decimal"
          min={0.5}
          max={24}
          step={0.5}
          label={t('lighting.new.hours')}
          helperText={t('lighting.new.hoursHint')}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          error={errors.hours}
          fullWidth
        />
        {create.isError && <Alert>{t('lighting.new.error')}</Alert>}
        <Inline gap="2" justify="end" wrap>
          <Button type="button" variant="ghost" onClick={onCancel}>{t('lighting.cancel')}</Button>
          <Button type="submit" isLoading={create.isPending}>{t('lighting.new.submit')}</Button>
        </Inline>
      </Stack>
    </form>
  );
}

export default NewSurveyForm;
